import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  Play, 
  Pause, 
  RotateCcw, 
  Brain, 
  User, 
  Mic,
  CheckCircle2,
  TrendingUp,
  Sparkles
} from "lucide-react";

export function InteractiveDemo() {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [typingText, setTypingText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showScoring, setShowScoring] = useState(false);
  const [scores, setScores] = useState({ hardSkills: 0, softSkills: 0, vibeCheck: 0 });
  const messagesContainerRef = useRef(null);
  const timeoutRef = useRef(null);

  // Demo conversation - memoized with translations
  const demoConversation = useMemo(() => [
    {
      type: "ai",
      message: t('demo.conversation.greeting', "Bonjour ! Je suis Ema, l'assistante IA de recrutement. Ravie de vous rencontrer pour ce poste. Comment allez-vous ?"),
      delay: 0
    },
    {
      type: "candidate",
      message: t('demo.conversation.candidateResponse1', "Bonjour Ema ! Je vais très bien, merci. Je suis enthousiaste pour cet entretien."),
      delay: 3000
    },
    {
      type: "ai",
      message: t('demo.conversation.question1', "Parfait ! Pouvez-vous me parler d'un projet technique complexe que vous avez réalisé récemment ?"),
      delay: 6000
    },
    {
      type: "candidate", 
      message: t('demo.conversation.candidateResponse2', "J'ai développé une plateforme e-commerce avec React et Node.js, gérant plus de 10 000 utilisateurs."),
      delay: 10000
    },
    {
      type: "ai",
      message: t('demo.conversation.question2', "Impressionnant ! Comment avez-vous géré la scalabilité avec autant d'utilisateurs ?"),
      delay: 14000
    },
    {
      type: "candidate",
      message: t('demo.conversation.candidateResponse3', "Nous avons utilisé Kubernetes pour le load balancing et Redis pour le caching."),
      delay: 18000
    },
    {
      type: "scoring",
      hardSkills: 87,
      softSkills: 82,
      vibeCheck: 9,
      delay: 22000
    }
  ], [t]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [displayedMessages, typingText]);

  useEffect(() => {
    if (!isPlaying) return;

    const processStep = () => {
      if (currentStep >= demoConversation.length) {
        setIsPlaying(false);
        return;
      }

      const step = demoConversation[currentStep];

      if (step.type === "scoring") {
        setShowScoring(true);
        animateScores(step);
        setCurrentStep(prev => prev + 1);
        return;
      }

      // Show typing indicator
      setIsTyping(true);

      // Simulate typing
      const message = step.message;
      let charIndex = 0;
      
      const typeInterval = setInterval(() => {
        if (charIndex < message.length) {
          setTypingText(message.substring(0, charIndex + 1));
          charIndex++;
        } else {
          clearInterval(typeInterval);
          setIsTyping(false);
          setTypingText("");
          setDisplayedMessages(prev => [...prev, { type: step.type, message }]);
          setCurrentStep(prev => prev + 1);
        }
      }, 30);

      return () => clearInterval(typeInterval);
    };

    const timeout = setTimeout(processStep, currentStep === 0 ? 500 : 1500);
    timeoutRef.current = timeout;

    return () => clearTimeout(timeout);
  }, [isPlaying, currentStep, demoConversation]);

  const animateScores = (step) => {
    const duration = 2000;
    const steps = 50;
    const interval = duration / steps;
    
    let currentIteration = 0;
    
    const scoreInterval = setInterval(() => {
      currentIteration++;
      const progress = currentIteration / steps;
      
      setScores({
        hardSkills: Math.round(step.hardSkills * progress),
        softSkills: Math.round(step.softSkills * progress),
        vibeCheck: Math.round(step.vibeCheck * progress)
      });
      
      if (currentIteration >= steps) {
        clearInterval(scoreInterval);
      }
    }, interval);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentStep(0);
    setDisplayedMessages([]);
    setTypingText("");
    setIsTyping(false);
    setShowScoring(false);
    setScores({ hardSkills: 0, softSkills: 0, vibeCheck: 0 });
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return (
    <section id="demo" className="py-24 px-6 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-purple-50 text-purple-700 border-purple-200">
            <Sparkles className="w-3 h-3 mr-1" />
            {t('demo.badge', 'Démo Interactive')}
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight" style={{ wordSpacing: '0.12em' }}>
            {t('demo.title', 'Voyez Ema en action')}
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {t('demo.subtitle', 'Découvrez comment notre IA mène un entretien professionnel')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Chat Interface */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border-0">
              {/* Header */}
              <div className="bg-slate-800 px-6 py-4 flex items-center justify-between border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{t('demo.interviewWith', 'Entretien avec Ema')}</p>
                    <p className="text-slate-400 text-sm">{t('demo.jobTitle', 'Développeur Full Stack')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isPlaying && (
                    <span className="flex items-center gap-2 text-cyan-400 text-sm">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                      {t('demo.inProgress', 'En cours')}
                    </span>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div 
                ref={messagesContainerRef}
                className="h-[350px] overflow-y-auto p-6 space-y-4"
              >
                {displayedMessages.map((msg, index) => (
                  <div 
                    key={index}
                    className={`flex gap-3 ${msg.type === 'candidate' ? 'flex-row-reverse' : ''}`}
                  >
                    <div 
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.type === 'ai' 
                          ? 'bg-gradient-to-br from-cyan-400 to-blue-500' 
                          : 'bg-slate-600'
                      }`}
                    >
                      {msg.type === 'ai' ? (
                        <Brain className="w-4 h-4 text-white" />
                      ) : (
                        <User className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div 
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.type === 'ai'
                          ? 'bg-slate-800 text-white'
                          : 'bg-cyan-600 text-white'
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{msg.message}</p>
                    </div>
                  </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-800 rounded-2xl px-4 py-3 max-w-[80%]">
                      <p className="text-sm text-white leading-relaxed">{typingText}<span className="animate-pulse">|</span></p>
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {displayedMessages.length === 0 && !isTyping && (
                  <div className="h-full flex flex-col items-center justify-center text-slate-500">
                    <Mic className="w-12 h-12 mb-4 opacity-50" />
                    <p>{t('demo.clickToStart', 'Cliquez sur "Lancer la démo" pour commencer')}</p>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="bg-slate-800 px-6 py-4 flex items-center justify-center gap-4 border-t border-slate-700">
                <Button
                  onClick={handleReset}
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="demo-reset-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {t('demo.restart', 'Recommencer')}
                </Button>
                <Button
                  onClick={handlePlayPause}
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8"
                  data-testid="demo-play-btn"
                >
                  {isPlaying ? (
                    <>
                      <Pause className="w-5 h-5 mr-2" />
                      {t('demo.pause', 'Pause')}
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      {currentStep > 0 ? t('demo.continue', 'Continuer') : t('demo.startDemo', 'Lancer la démo')}
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>

          {/* Scoring Panel */}
          <div className="space-y-6">
            <Card className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
                <h3 className="font-semibold text-slate-900 tracking-wide">{t('demo.realtimeScoring', 'Scoring en temps réel')}</h3>
              </div>

              <div className="space-y-5">
                {/* Hard Skills */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600">{t('reports.scores.hardSkills', 'Hard Skills')}</span>
                    <span className="text-sm font-semibold text-slate-900">{scores.hardSkills}/100</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-300"
                      style={{ width: `${scores.hardSkills}%` }}
                    />
                  </div>
                </div>

                {/* Soft Skills */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600">{t('reports.scores.softSkills', 'Soft Skills')}</span>
                    <span className="text-sm font-semibold text-slate-900">{scores.softSkills}/100</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
                      style={{ width: `${scores.softSkills}%` }}
                    />
                  </div>
                </div>

                {/* Vibe Check */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600">{t('reports.scores.vibeCheck', 'Vibe Check')}</span>
                    <span className="text-sm font-semibold text-slate-900">{scores.vibeCheck}/10</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-300"
                      style={{ width: `${scores.vibeCheck * 10}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Verdict */}
              {showScoring && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t('reports.verdict', 'Verdict')}</span>
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {t('reports.recommended', 'Recommandé')}
                    </Badge>
                  </div>
                </div>
              )}
            </Card>

            {/* Info Card */}
            <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-100">
              <h4 className="font-semibold text-slate-900 mb-3 tracking-wide">{t('demo.whatYouSee', 'Ce que vous voyez')}:</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                  {t('demo.feature1', 'Questions adaptées au profil')}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                  {t('demo.feature2', 'Follow-up intelligent')}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                  {t('demo.feature3', 'Scoring en temps réel')}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                  {t('demo.feature4', 'Verdict automatique')}
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

export default InteractiveDemo;
